import { Injectable, signal } from '@angular/core';

import { CustomTreeNode } from '../../models/custom-tree-node.model';

@Injectable()
export class TreeSelectionManagerService {
  private globalSelectedIds = new Set<number>();

  siteData = signal<CustomTreeNode[]>([]);
  expandedState = signal<Map<string, boolean>>(new Map());
  filterSites = signal<number[]>([]);

  getSelectedIdsArray(): number[] {
    return Array.from(this.globalSelectedIds);
  }

  setSiteData(data: CustomTreeNode[]): void {
    this.siteData.set(data);
  }

  setFilterSites(sites: number[]): void {
    this.filterSites.set(sites);
  }

  reset(): void {
    this.expandedState.set(new Map());
    this.filterSites.set([]);
    this.globalSelectedIds.clear();
  }

  onExpandedStateChange(map: Map<string, boolean>): void {
    this.expandedState.set(map);
  }

  handleSelectionChange(
    event: {
      changedNode?: CustomTreeNode;
      checked?: boolean;
      selectAll?: boolean;
      siteIds?: number[];
    },
    treeData: CustomTreeNode[],
  ): number[] {
    if (event.selectAll !== undefined) {
      this.handleSelectAll(event.selectAll, treeData, event.siteIds);
    } else if (event.changedNode) {
      this.handleNodeSelection(event.changedNode, event.checked ?? false);
    }

    const selectedIds = this.getSelectedIdsArray();
    this.filterSites.set(selectedIds);

    return selectedIds;
  }

  private handleSelectAll(
    selectAll: boolean,
    treeData: CustomTreeNode[],
    siteIds?: number[],
  ): void {
    if (siteIds) {
      siteIds.forEach((id) =>
        selectAll
          ? this.globalSelectedIds.add(id)
          : this.globalSelectedIds.delete(id),
      );

      return;
    }

    if (selectAll) {
      this.globalSelectedIds = new Set(this.getAllSiteIds(treeData));
    } else {
      this.globalSelectedIds.clear();
    }
  }

  private handleNodeSelection(node: CustomTreeNode, checked: boolean): void {
    if (node.children && node.children.length > 0) {
      const descendantIds = this.getAllDescendantLeafIds(node);
      descendantIds.forEach((id) =>
        checked
          ? this.globalSelectedIds.add(id)
          : this.globalSelectedIds.delete(id),
      );
    } else {
      if (checked) {
        this.globalSelectedIds.add(node.data);
      }

      if (!checked) {
        this.globalSelectedIds.delete(node.data);
      }
    }
  }

  private getAllDescendantLeafIds(node: CustomTreeNode): number[] {
    if (!node.children || node.children.length === 0) {
      return [node.data];
    }

    return node.children.flatMap((child) =>
      this.getAllDescendantLeafIds(child),
    );
  }

  private getAllSiteIds(nodes: CustomTreeNode[]): number[] {
    return nodes.flatMap((node) => this.getAllDescendantLeafIds(node));
  }
}
